import { create } from 'zustand';

// Any key from QUICK_PRODUCTS (quickProducts.ts). Kept as string so adding a
// product there never requires touching this store.
export type QuickSubscribeProduct = string;

export interface QuickPreset {
  /** Pre-toggle the Analyzer add-on on open (Analyzer card CTA). */
  analyzer?: boolean;
  /** Pre-select the Premium tier on open (Premium row CTA). */
  tier?: 'premium';
  /** Pre-select a product mode on open (e.g. futures 'unlimited'). */
  mode?: string;
  /** Pre-select a billing cycle (the tour's ticket carries the user's choice). */
  cycle?: string;
  /** CFD accounts on the cart (the ticket's count). */
  accounts?: number;
  /** Futures accounts beyond the included bundle. */
  futuresExtra?: number;
}

interface QuickSubscribeStore {
  open: boolean;
  product: QuickSubscribeProduct;
  presetAnalyzer: boolean;
  presetTier: 'premium' | null;
  presetMode: string | null;
  presetCycle: string | null;
  presetAccounts: number | null;
  presetFuturesExtra: number | null;
  /** Set when the current cart came from the quick modal, so the purchase
   *  event can attribute the sale to that surface rather than the drawer. */
  lastEntry: 'quick_subscribe' | 'ticket' | null;
  /** Where to send the user when the checkout drawer closes — set by
   *  tour-originated checkouts so paying (or bailing) never strands them
   *  on the dashboard with the tour silently gone. One-shot. */
  returnTo: string | null;
  setReturnTo: (v: string | null) => void;
  openQuickSubscribe: (product?: QuickSubscribeProduct, preset?: QuickPreset) => void;
  markEntry: (v: 'quick_subscribe' | 'ticket' | null) => void;
  close: () => void;
}

/**
 * Imperative open/close for the quick-subscribe dialog (same pattern as
 * useCheckoutStore): any locked surface can call
 * `useQuickSubscribe.getState().openQuickSubscribe('cfd')` without a provider.
 * The dialog itself mounts once in MainLayout.
 */
export const useQuickSubscribe = create<QuickSubscribeStore>()((set) => ({
  open: false,
  product: 'cfd',
  presetAnalyzer: false,
  presetTier: null,
  presetMode: null,
  presetCycle: null,
  presetAccounts: null,
  presetFuturesExtra: null,
  lastEntry: null,
  returnTo: null,
  setReturnTo: (returnTo) => set({ returnTo }),
  // Switching the product from inside the dialog passes no preset and must
  // not wipe the cycle and counts the user arrived with.
  openQuickSubscribe: (product, preset) =>
    set((s) =>
      preset === undefined
        ? { open: true, product: product ?? s.product }
        : {
            open: true,
            product: product ?? s.product,
            presetAnalyzer: Boolean(preset.analyzer),
            presetTier: preset.tier === 'premium' ? 'premium' : null,
            presetMode: preset.mode ?? null,
            presetCycle: preset.cycle ?? null,
            presetAccounts: preset.accounts ?? null,
            presetFuturesExtra: preset.futuresExtra ?? null
          }
    ),
  markEntry: (lastEntry) => set({ lastEntry }),
  close: () => set({ open: false })
}));
